import React, { useEffect, useState } from "react";

import { MenuItem } from "@mui/material";
import { Stack } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiCalendar, FiSettings } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useTimezoneDate from "../../../hooks/useTimezoneDate";
import {
  ESupplyChainOverviewTabs,
  SupplyChainOverviewConfigure,
} from "../../../models/supply.chain.overview.configure";
import {
  DashboardPeriodOptions,
  ResourceGroup,
  ResourceName,
  ResourceSubGroup,
} from "../../../util/enums";
import { getDateIntervalByPeriod } from "../../../util/getDateIntervalByperiod";
import { getResourceByNameGroup } from "../../../util/getResourceByNameGroup";
import { Button } from "../../Form";
import { StyledMenu as Menu } from "../../Generic/AnchorMenu";
import LoadingSkeleton from "../../Generic/LoadingSkeleton";
import UITabMaterial, { PanelProps } from "../../Generic/UITabMaterial";
import { ConfigureSupplyChainOverview } from "./Configure";
import { useSupplyChainOverviewContext } from "./context";
import {
  setLocalStorageLastCustomDate,
  setLocalStorageLastCustomPeriod,
  setLocalStorageLastPeriod,
  setLocalStorageLastTab,
} from "./LocalStorageConfig";
import ContainersOverviewPanel from "./Panels/Containers";
import OrdersOverviewPanel from "./Panels/Orders";
import ProductsOverviewPanel from "./Panels/Products";
import ShipmentsOverviewPanel from "./Panels/Shipments";
import CustomPeriodModal from "./Period";
import { useStyles } from "./styles";

const OverviewTabsIndex = {
  [ESupplyChainOverviewTabs.ORDERS]: 0,
  [ESupplyChainOverviewTabs.PRODUCTS]: 1,
  [ESupplyChainOverviewTabs.SHIPMENTS]: 2,
  [ESupplyChainOverviewTabs.CONTAINERS]: 3,
};

const SupplyChainOverview = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const {
    config,
    customDate,
    customPeriod,
    selectedPanel,
    selectedPeriod,
    setSelectedPanel,
    setSelectedPeriod,
  } = useSupplyChainOverviewContext();

  const [configureOpened, setConfigureOpened] = useState<boolean>(false);
  const [openCustomPeriod, setOpenCustomPeriod] = useState<boolean>(false);
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<null | HTMLElement>(null);

  const containersPanels: PanelProps = {
    key: "containers" as keyof SupplyChainOverviewConfigure,
    menuItem: translate("containers"),
    render: () => <ContainersOverviewPanel />,
    skeleton: () => <LoadingSkeleton />,
  };

  const ordersPanels: PanelProps = {
    key: "orders" as keyof SupplyChainOverviewConfigure,
    menuItem: translate("orders"),
    render: () => <OrdersOverviewPanel />,
    skeleton: () => <LoadingSkeleton />,
  };

  const productsPanels: PanelProps = {
    key: "products" as keyof SupplyChainOverviewConfigure,
    menuItem: translate("products"),
    render: () => <ProductsOverviewPanel />,
    skeleton: () => <LoadingSkeleton />,
  };

  const shipmentsPanels: PanelProps = {
    key: "shipments" as keyof SupplyChainOverviewConfigure,
    menuItem: translate("shipments"),
    render: () => <ShipmentsOverviewPanel />,
    skeleton: () => <LoadingSkeleton />,
  };

  const handleSelectPeriod = (period: DashboardPeriodOptions) => {
    setSelectedPeriod(period);
    setPeriodMenuAnchor(null);
    setOpenCustomPeriod(
      period === DashboardPeriodOptions.CUSTOM_DATE ||
        period === DashboardPeriodOptions.CUSTOM_PERIOD
    );
  };

  const getPeriodBtnLabel = () => {
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_DATE && customDate) {
      return getDateValueUtcTimezone(customDate.toISOString(), "dd/MM/yyyy");
    }
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_PERIOD && customPeriod) {
      const from = format(customPeriod.from, "dd/MM/yyyy");
      const to = format(customPeriod.to, "dd/MM/yyyy");

      return `${from} - ${to}`;
    }

    return getDateIntervalByPeriod({ selectedPeriod });
  };

  const rightComponent = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button
          className={classes.btnPeriod}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setPeriodMenuAnchor(event.currentTarget);
          }}
          startIcon={<FiCalendar />}
          variant="outlined"
        >
          {getPeriodBtnLabel()}
        </Button>
        <Menu
          anchorEl={periodMenuAnchor}
          onClose={() => setPeriodMenuAnchor(null)}
          open={Boolean(periodMenuAnchor)}
          variant="selectedMenu"
        >
          {Object.values(DashboardPeriodOptions).map((option: DashboardPeriodOptions) => (
            <MenuItem
              key={option}
              onClick={() => handleSelectPeriod(option)}
              selected={option === selectedPeriod}
            >
              {translate(option)}
            </MenuItem>
          ))}
        </Menu>
        <Button
          className={classes.btnConfigure}
          onClick={() => setConfigureOpened(true)}
          startIcon={<FiSettings />}
          variant="outlined"
        >
          {translate("configure")}
        </Button>
      </Stack>
    );
  };

  useEffect(() => {
    if (selectedPanel && selectedPeriod) {
      history.replace(`${PRIVATE_ROUTES.OVERVIEW}?tab=${selectedPanel}&period=${selectedPeriod}`);
      setLocalStorageLastTab(selectedPanel);
      setLocalStorageLastPeriod(selectedPeriod);
    }
    setLocalStorageLastCustomDate(customDate);
    setLocalStorageLastCustomPeriod(customPeriod);
  }, [selectedPanel, selectedPeriod, customDate, customPeriod]);

  if (!config) return null;

  const containersPermission = getResourceByNameGroup(
    ResourceGroup.CONTAINERS,
    ResourceSubGroup.CONTAINERS,
    ResourceName.CONTAINERS,
    currentUser?.resources
  );

  const ordersPermission = getResourceByNameGroup(
    ResourceGroup.ORDERS,
    ResourceSubGroup.ORDERS,
    ResourceName.ORDERS,
    currentUser?.resources
  );

  const productsPermission = getResourceByNameGroup(
    ResourceGroup.BUSINESS,
    ResourceSubGroup.BUSINESS,
    ResourceName.PRODUCTS,
    currentUser?.resources
  );

  const shipmentsPermission = getResourceByNameGroup(
    ResourceGroup.SHIPMENTS,
    ResourceSubGroup.SHIPMENTS,
    ResourceName.SHIPMENTS,
    currentUser?.resources
  );

  const leftPanels: PanelProps[] = [];

  if (ordersPermission?.roles_resources?.view) {
    leftPanels.push(ordersPanels);
  }
  if (productsPermission?.roles_resources?.view) {
    leftPanels.push(productsPanels);
  }
  if (shipmentsPermission?.roles_resources?.view) {
    leftPanels.push(shipmentsPanels);
  }
  if (containersPermission?.roles_resources?.view) {
    leftPanels.push(containersPanels);
  }

  return (
    <>
      <CustomPeriodModal opened={openCustomPeriod} setOpened={setOpenCustomPeriod} />
      <UITabMaterial
        className={classes.tabPanel}
        leftPanels={leftPanels}
        selectedLeftPanel={OverviewTabsIndex[selectedPanel] || 0}
        onChangeLeftPanel={(_event, _panel, key) => {
          if (key) setSelectedPanel(key as keyof SupplyChainOverviewConfigure);
        }}
        rightComponent={rightComponent}
      />
      <ConfigureSupplyChainOverview isOpened={configureOpened} setIsOpened={setConfigureOpened} />
    </>
  );
};

export default SupplyChainOverview;
